import { useAuthContext } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, Checkbox, ErrorMessage, ErrorMessagePage, Input, LoaderPage } from '@core/ui';
import { getRouteApi, useNavigate } from '@online-webinar/router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { CountryDropdown } from 'react-country-region-selector';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { usePasswordValidator, useValidations, useSubmitValidation } from '../../../hooks';
import { AccountWizardState } from '../../../types';
import { AuthPageTitle, PasswordEyeIcon, TermsAndCondition } from '../../atoms';
import { AuthPage, AuthPageContent, RegisterSideContent } from '../../organism';

const route = getRouteApi('/r/$accountInviteId');
export const AccountInvitePage = () => {
    const { accountInviteId } = route.useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { updateAuthState } = useAuthContext();

    const {
        register,
        formState: { errors },
        getValues,
        getFieldState,
        handleSubmit,
        trigger,
        reset,
        setValue,
        watch
    } = useForm<AccountWizardState>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: {} });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<AccountWizardState> = (data) => {};

    const { passwordValidation, validatePassword, passwordMessages } = usePasswordValidator();

    const { companyNameValidation, firstNameValidation, lastNameValidation, nicknameValidation, termsOfAgreementValidation } = useValidations();

    const [passwordType, setPasswordType] = useState('password');
    const [passwordScore, setPasswordScore] = useState<number>(-1);

    const countryId = useId();

    const togglePasswordVisibility = () => {
        if (passwordType === 'password') {
            setPasswordType('text');
        } else {
            setPasswordType('password');
        }
    };

    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const countryCode = watch('countryCode');
    useEffect(() => {
        const nickNameState = getFieldState('nickname');
        if (nickNameState.isDirty) return;
        const nickname = `${firstName ?? ''} ${lastName ?? ''}`.trim();
        setValue('nickname', nickname, { shouldDirty: false, shouldTouch: true });
    }, [firstName, lastName]);

    const password = watch('password');
    useEffect(() => {
        if (password) {
            validatePassword(password).then((result) => setPasswordScore(result.score));
        } else {
            setPasswordScore(-1);
        }
    }, [password]);

    const { data, error, isLoading } = useQuery(
        OptionsApi.accountInvite.get(accountInviteId, {
            refetchOnReconnect: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false
        })
    );

    useEffect(() => {
        if (!data) {
            return;
        }

        reset(data);
        // Special case for nickname, otherwise it'll be overwritten by the useEffect above
        if (data.nickname) {
            setTimeout(() => {
                setValue('nickname', data.nickname, { shouldDirty: true, shouldTouch: true });
            }, 10);
        }
    }, [data]);

    const acceptInviteMutation = useMutation(OptionsApi.accountInvite.accept());
    const acceptInvite = () => {
        // All fields at once, in the order of the form: every error shows and focus goes to the first invalid field
        validate(['firstName', 'lastName', 'nickname', 'companyName', 'password', 'termsOfAgreement']).then((isValid) => {
            if (!isValid) return;

            acceptInviteMutation.mutate(
                {
                    accountInviteId,
                    register: getValues()
                },
                {
                    onSuccess: (response) => {
                        updateAuthState(response);
                        navigate({ to: '/' });
                    }
                }
            );
        });
    };
    const rejectInviteMutation = useMutation(OptionsApi.accountInvite.reject());
    const rejectInvite = () => {
        rejectInviteMutation.mutate(accountInviteId, {
            onSuccess: () => {
                navigate({ to: '/' });
            }
        });
    };

    if (error) {
        return <ErrorMessagePage apiError={error} errorSubject="AccountInvite" />;
    }

    if (!data || isLoading) {
        return <LoaderPage />;
    }

    const mutationError = acceptInviteMutation.error || rejectInviteMutation.error;
    return (
        <AuthPage>
            <AuthPageContent>
                <AuthPageTitle className="mb-8">{t('AccountInvite.PageHeading')}</AuthPageTitle>
                {mutationError && <ErrorMessage errorSubject="Account.ApiErrorCode" apiError={mutationError} classNames="mb-6" />}
                <p className="mb-8">
                    <Trans i18nKey="AccountInvite.Info" />
                </p>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        autoFocus
                        label={t('Account.Label.FirstName')}
                        {...register('firstName', firstNameValidation)}
                        errorMessage={errors.firstName?.message}
                    ></Input>
                    <Input label={t('Account.Label.LastName')} {...register('lastName', lastNameValidation)} errorMessage={errors.lastName?.message}></Input>
                    <Input label={t('Account.Label.Nickname')} {...register('nickname', nicknameValidation)} errorMessage={errors.nickname?.message}></Input>
                    <Input
                        label={t('Account.Label.CompanyName')}
                        {...register('companyName', companyNameValidation)}
                        errorMessage={errors.companyName?.message}
                    ></Input>
                    <div className="group relative w-full clear-both mb-10">
                        <label htmlFor={countryId} className="block text-sm">
                            {t('Account.Label.Country')}
                        </label>
                        <div className="flex justify-between">
                            <CountryDropdown
                                id={countryId}
                                className="w-full bg-transparent outline-hidden py-input-vert text-base"
                                value={countryCode ?? ''}
                                onChange={(val) => setValue('countryCode', val)}
                                valueType="short"
                            />
                        </div>
                        <div className={`bg-black group-hover:bg-primary group-focus-within:bg-primary input-border`} />
                    </div>

                    <Input type="email" label={t('Account.Label.Email')} value={data.emailAddress} readOnly disabled></Input>
                    <Input
                        type={passwordType}
                        label={t('Account.Label.Password')}
                        {...register('password', passwordValidation)}
                        {...passwordMessages(passwordScore, errors.password?.message)}
                    >
                        <PasswordEyeIcon passwordType={passwordType} onClick={togglePasswordVisibility} />
                    </Input>
                    <Checkbox
                        id="termsOfAgreement"
                        label={<TermsAndCondition />}
                        errorMessage={errors.termsOfAgreement?.message}
                        classNames="mb-10"
                        {...register('termsOfAgreement', termsOfAgreementValidation)}
                    />
                    <div className="clear-both flex flex-wrap gap-4 clear-both mb-8">
                        <Button
                            type="button"
                            onClick={rejectInvite}
                            isLoading={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                            variant="primary-outline"
                            classNames="flex-1 whitespace-nowrap"
                        >
                            {t('AccountInvite.Button.Reject')}
                        </Button>
                        <Button
                            type="submit"
                            onClick={acceptInvite}
                            isLoading={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                            variant="primary"
                            classNames="flex-1 whitespace-nowrap"
                        >
                            {t('Account.Button.Register')}
                        </Button>
                    </div>
                </form>
            </AuthPageContent>
            <RegisterSideContent />
        </AuthPage>
    );
};
